'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { hydrateEmbeddedComponentData } from './embedded-component';
import {
    MEGA_MENU_NAVIGATION_RESOURCE,
    type MegaMenuNavigationLoaderData,
    type MegaMenuTrendingContent,
} from './mega-menu-loader-data';

type UseMegaMenuContentOptions = {
    /** Prefetch when the desktop mega menu bar is visible (custom labels). */
    enabled: boolean;
    /** Dropdown is open — load navigation data if needed and expose category fields. */
    open: boolean;
};

type UseMegaMenuContentResult = {
    brandsCategory: ShopperProducts.schemas['Category'] | undefined;
    newInCategory: ShopperProducts.schemas['Category'] | undefined;
    trendingContent: MegaMenuTrendingContent | undefined;
    megaMenuEmbedded: ComponentWithComponentData | undefined;
    isLoading: boolean;
    hasLoaded: boolean;
};

type LoadAttempt = 'prefetch' | 'open';

export function useMegaMenuContent({ enabled, open }: UseMegaMenuContentOptions): UseMegaMenuContentResult {
    const fetcher = useFetcher<MegaMenuNavigationLoaderData>({ key: 'mega-menu-navigation' });
    const attemptedKeyRef = useRef<LoadAttempt | null>(null);

    useEffect(() => {
        // Clear a failed/in-flight open attempt when the dropdown closes so a later open can retry.
        // Downgrade to 'prefetch' (not null) so an enabled bar does not immediately re-fire a background load.
        if (!open && attemptedKeyRef.current === 'open') {
            attemptedKeyRef.current = 'prefetch';
        }

        const shouldLoad = enabled || open;
        if (!shouldLoad || fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        const nextAttempt: LoadAttempt = open ? 'open' : 'prefetch';
        if (attemptedKeyRef.current === 'open') {
            return;
        }
        if (attemptedKeyRef.current === 'prefetch' && nextAttempt === 'prefetch') {
            return;
        }

        attemptedKeyRef.current = nextAttempt;
        void fetcher.load(MEGA_MENU_NAVIGATION_RESOURCE);
    }, [enabled, open, fetcher, fetcher.data, fetcher.state, fetcher.load]);

    const megaMenuEmbedded = useMemo(
        () =>
            fetcher.data?.megaMenuEmbedded ? hydrateEmbeddedComponentData(fetcher.data.megaMenuEmbedded) : undefined,
        [fetcher.data?.megaMenuEmbedded]
    );

    return useMemo(
        () => ({
            brandsCategory: open ? fetcher.data?.brandsCategory : undefined,
            newInCategory: open ? fetcher.data?.newInCategory : undefined,
            trendingContent: open ? fetcher.data?.trendingContent : undefined,
            megaMenuEmbedded,
            isLoading: open && fetcher.state !== 'idle',
            hasLoaded: Boolean(fetcher.data),
        }),
        [open, fetcher.data, fetcher.state, megaMenuEmbedded]
    );
}
